import * as THREE from 'three';
import { fxUniforms, quadGeometry } from '../fx/common';
import { noiseTexture } from '../fx/noise';
import { L } from '../game/config';

/** A continuous off-screen fog bank, plus low wisps at the emergence edge.
 * Fourteen static instances, one draw, world-sized noise and no per-frame uploads.
 * `lite` (low tier) keeps the silhouette with eight instances and no curl,
 * roughly halving the fog's overdraw on fill-rate-bound GPUs.
 */
/** Extra reach of the fog frontier toward the gate (m): narrow portrait frames crop the bank, so it leans in. */
export const fogReach = { value: 0 };

export function buildSpawnFog(scene: THREE.Scene, lite = false) {
  const geometry = quadGeometry();
  const centers: number[] = [], shapes: number[] = [];
  const sheet = (x: number, y: number, z: number, w: number, h: number, opacity: number, ground: boolean) => {
    centers.push(x, y, z, ground ? 1 : 0);
    shapes.push(w, h, opacity, centers.length * 0.037);
  };
  // Wide ground blankets reach beyond ultrawide viewports. Their right edge is
  // clipped in world space, so changing the aspect never exposes a round blob.
  // One wide ground blanket carrying the combined opacity of two overlapping ones (measured: the second
  // blanket was 0.3 of a screen of extra fill for a bank that is already ~90% opaque where both lay).
  sheet(-28, 0.045, L.roadZ - 1, 52, 26, 0.86, true);
  sheet(-7.8, 0.095, L.roadZ, 11, 14, 0.56, true);
  if (lite) {
    sheet(-26, 1.1, L.roadZ - 1.5, 46, 7.5, 0.7, false);
  } else {
    sheet(-26, 1.1, L.roadZ - 3, 46, 6.5, 0.62, false);
    sheet(-25, 0.7, L.roadZ + 2.5, 44, 5, 0.52, false);
  }
  // Sheets between the camera and the road, so enemies walk inside the bank rather than over it.
  sheet(-24, 0.75, L.roadZ + 1.7, 40, 3.2, lite ? 0.55 : 0.5, false);
  if (!lite) {
    sheet(L.fogStartX - 0.2, 0.55, L.roadZ + 0.9, 4.2, 1.9, 0.42, false);
    sheet(L.fogStartX - 0.9, 0.6, L.roadZ + 2.2, 4.6, 2.2, 0.4, false);
  }
  // A broken, rolling frontier rather than a perfectly straight screen wipe.
  const wisps = lite ? 4 : 7, spacing = 12 / wisps;
  for (let i = 0; i < wisps; i++) {
    const z = L.roadZ - 6 + i * spacing + (lite ? spacing * 0.3 : 0);
    sheet(L.fogStartX - 0.4 + Math.sin(i * 2.3) * 0.35, 0.65 + (i % 2) * 0.2, z,
      lite ? 5.6 : 4.9, (lite ? 2.8 : 2.5) + (i % 3) * 0.35, 0.48 + (i % 2) * 0.08, false);
  }
  geometry.setAttribute('iCenter', new THREE.InstancedBufferAttribute(new Float32Array(centers), 4));
  geometry.setAttribute('iShape', new THREE.InstancedBufferAttribute(new Float32Array(shapes), 4));
  geometry.instanceCount = centers.length / 4;
  const material = new THREE.ShaderMaterial({
    uniforms: { uTime: fxUniforms.uTime, uMap: { value: noiseTexture() }, uColor: { value: new THREE.Color(0x485361) },
      uEdge: { value: new THREE.Vector2(L.fogStartX - 0.8, L.fogEndX + 0.1) }, uReach: fogReach },
    transparent: true, depthWrite: false,
    defines: lite ? { LITE: '' } : {},
    vertexShader: /* glsl */ `
      attribute vec4 iCenter, iShape;
      varying vec2 vUv, vNoise; varying vec3 vShape; varying float vWorldX, vWorldZ;
      void main() {
        vUv = uv; vShape = vec3(iShape.zw, iCenter.w);
        vec3 p = iCenter.xyz;
        vec4 mv;
        if (iCenter.w < 0.5) {
          // Billboard position reconstructed in world space for the fog frontier.
          vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
          vec3 up = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
          p += right * position.x * iShape.x + up * position.y * iShape.y;
          vNoise = vec2(p.x, p.y + iCenter.z * 0.65) * 0.18;
        } else {
          p += vec3(position.x * iShape.x, 0.0, -position.y * iShape.y);
          vNoise = p.xz * 0.16;
        }
        vWorldX = p.x; vWorldZ = p.z;
        mv = modelViewMatrix * vec4(p, 1.0);
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime; uniform sampler2D uMap; uniform vec3 uColor; uniform vec2 uEdge; uniform float uReach;
      varying vec2 vUv, vNoise; varying vec3 vShape; varying float vWorldX, vWorldZ;
      void main() {
        vec2 q = abs(vUv * 2.0 - 1.0);
        float edge = (1.0 - smoothstep(0.58, 1.0, q.x)) * (1.0 - smoothstep(0.45, 1.0, q.y));
        // the frontier rolls: slow tongues reach out along z and pull back, so the bank never reads as a wall
        float reach = sin(vWorldZ * 0.7 + uTime * 0.23) * 0.75 + sin(vWorldZ * 1.9 - uTime * 0.31 + 1.7) * 0.35;
        float frontierX = vWorldX - reach - uReach;
        // Skip the transparent outskirts before fetching the texture.
        if (edge * (1.0 - smoothstep(uEdge.x, uEdge.y + 1.4, frontierX)) < 0.003) discard;
        vec2 drift = vec2(uTime * 0.013 + vShape.y, -uTime * 0.007);
        #ifdef LITE
          vec2 fields = texture2D(uMap, vNoise + drift).rg;
        #else
          vec2 curl = vec2(sin(vNoise.y * 6.28 + uTime * 0.09), cos(vNoise.x * 6.28 - uTime * 0.07)) * 0.065;
          vec2 fields = texture2D(uMap, vNoise + drift + curl).rg;
        #endif
        float density = smoothstep(0.23, 0.72, fields.x * 0.65 + fields.y * 0.35);
        // the noise also frays the frontier, tearing it into wisps
        float frontier = 1.0 - smoothstep(uEdge.x, uEdge.y, frontierX - (fields.y - 0.5) * 2.8);
        // Ground mist holds a quiet base; the higher sheets supply rolling detail.
        density = mix(density, 0.25 + density * 0.75, vShape.z);
        // thick billows catch the moonlight, thin veils sink into shadow: depth without extra layers
        gl_FragColor = vec4(uColor * (0.55 + density * 0.55), edge * frontier * density * vShape.x);
      }`,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'spawnFog'; mesh.frustumCulled = false; mesh.renderOrder = 6;
  scene.add(mesh);
}
